import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage, getBlobErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { DocumentActionRow } from "@/components/DocumentActionRow";

const DEACTIVATE_ROLES = ["Director", "Manager"];

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">{label}</p>
      <p className="text-sm text-slate-800 font-medium mt-0.5">{value || "—"}</p>
    </div>
  );
}

export default function SiteDetailPanel({ site: s, onClose, onEdit, canManage, onUpdated }) {
  const { user } = useAuth();
  const canDeactivate = DEACTIVATE_ROLES.includes(user?.role);
  const [busy, setBusy] = useState(false);
  const [busyDoc, setBusyDoc] = useState(null);
  const [woLabel, setWoLabel] = useState("");
  const [otherLabel, setOtherLabel] = useState("");
  const [uploading, setUploading] = useState(null);

  const refresh = async () => {
    const res = await client.get(`/site-master/sites/${s.id}`);
    onUpdated(res.data);
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      const path = s.active === false ? "reactivate" : "deactivate";
      const res = await client.post(`/site-master/sites/${s.id}/${path}`);
      toast.success(s.active === false ? "Site reactivated" : "Site deactivated");
      onUpdated(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadWorkOrder = async (file) => {
    setUploading("wo");
    const fd = new FormData();
    fd.append("file", file);
    if (woLabel.trim()) fd.append("label", woLabel.trim());
    try {
      await client.post(`/site-master/sites/${s.id}/work-orders`, fd);
      toast.success("Work Order uploaded");
      setWoLabel("");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(null);
    }
  };

  const uploadOtherDoc = async (file) => {
    if (!otherLabel.trim()) return toast.error("Label is required for Other Documents");
    setUploading("other");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", otherLabel.trim());
    try {
      await client.post(`/site-master/sites/${s.id}/other-documents`, fd);
      toast.success("Document uploaded");
      setOtherLabel("");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(null);
    }
  };

  const viewWorkOrder = async (docId) => {
    try {
      const res = await client.get(`/site-master/sites/${s.id}/work-orders/${docId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(await getBlobErrorMessage(err));
    }
  };

  const deleteWorkOrder = async (docId) => {
    if (!window.confirm("Delete this document? It will be removed from the list.")) return;
    setBusyDoc(docId);
    try {
      await client.delete(`/site-master/sites/${s.id}/work-orders/${docId}`);
      toast.success("Document deleted");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyDoc(null);
    }
  };

  const viewOtherDoc = async (docId) => {
    try {
      const res = await client.get(`/site-master/sites/${s.id}/other-documents/${docId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(await getBlobErrorMessage(err));
    }
  };

  const deleteOtherDoc = async (docId) => {
    if (!window.confirm("Delete this document? It will be removed from the list.")) return;
    setBusyDoc(docId);
    try {
      await client.delete(`/site-master/sites/${s.id}/other-documents/${docId}`);
      toast.success("Document deleted");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyDoc(null);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="site-detail-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {s.display_label || s.label}
            <Badge variant="outline" className="font-mono">
              {s.site_id || s.code}
            </Badge>
          </SheetTitle>
          <SheetDescription>
            {s.client_short_name} · {s.job_work_type}
          </SheetDescription>
          <div className="flex gap-2 pt-2">
            {canManage && (
              <Button size="sm" data-testid="site-detail-edit-button" onClick={() => onEdit(s)} className="bg-amber-600 hover:bg-amber-700">
                Edit
              </Button>
            )}
            {canDeactivate && (
              <Button size="sm" variant="outline" disabled={busy} data-testid="site-detail-toggle-active-button" onClick={toggleActive}>
                {s.active === false ? "Reactivate" : "Deactivate"}
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="grid grid-cols-2 gap-4 pt-6">
          <Field label="Site Value" value={s.label} />
          <Field label="Client (Short Name)" value={s.client_short_name} />
          <Field label="Client (Full Name)" value={s.client_name_full} />
          <Field label="State" value={s.state} />
          <Field label="Job Work / Rental Type" value={s.job_work_type} />
          <Field label="Site Status" value={s.site_status} />
          <Field label="Support Method" value={s.support_method} />
          <Field label="Rate Structure" value={s.rate_structure} />
          <Field label="Active" value={s.active === false ? "Deactivated" : "Yes"} />
        </div>

        <div className="pt-6 space-y-4">
          <div className="rounded-md border border-slate-200 p-3 space-y-2">
            <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">
              Work Orders ({(s.work_orders || []).length})
            </p>
            <div className="space-y-1.5">
              {(s.work_orders || []).length === 0 && (
                <p className="text-xs text-slate-400" data-testid="site-wo-empty">No Work Orders uploaded.</p>
              )}
              {(s.work_orders || []).map((w) => (
                <DocumentActionRow
                  key={w.doc_id}
                  testId={`site-wo-${w.doc_id}`}
                  label={w.label || "Work Order"}
                  filename={w.filename}
                  meta={w.uploaded_at ? new Date(w.uploaded_at).toLocaleDateString("en-IN") : null}
                  canDelete={canManage}
                  busy={busyDoc === w.doc_id}
                  onView={() => viewWorkOrder(w.doc_id)}
                  onDelete={() => deleteWorkOrder(w.doc_id)}
                />
              ))}
            </div>
            {canManage && (
              <div className="flex items-center gap-2 pt-1">
                <Input
                  data-testid="site-detail-wo-label-input"
                  placeholder="Label (optional)"
                  value={woLabel}
                  onChange={(e) => setWoLabel(e.target.value)}
                  className="h-8 text-xs max-w-[220px]"
                />
                <label
                  className={`inline-flex items-center gap-1.5 text-xs cursor-pointer ${uploading === "wo" ? "text-slate-300" : "text-amber-600 hover:text-amber-700"}`}
                  data-testid="site-detail-wo-upload"
                >
                  <Upload className="h-3.5 w-3.5" /> Upload
                  <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" disabled={uploading === "wo"} onChange={(ev) => ev.target.files[0] && uploadWorkOrder(ev.target.files[0])} />
                </label>
              </div>
            )}
          </div>
          <div className="rounded-md border border-slate-200 p-3 space-y-2">
            <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">
              Other Documents ({(s.other_documents || []).length})
            </p>
            <div className="space-y-1.5">
              {(s.other_documents || []).length === 0 && (
                <p className="text-xs text-slate-400" data-testid="site-other-doc-empty">No documents uploaded.</p>
              )}
              {(s.other_documents || []).map((d) => (
                <DocumentActionRow
                  key={d.doc_id}
                  testId={`site-other-doc-${d.doc_id}`}
                  label={d.label}
                  filename={d.filename}
                  meta={d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("en-IN") : null}
                  canDelete={canManage}
                  busy={busyDoc === d.doc_id}
                  onView={() => viewOtherDoc(d.doc_id)}
                  onDelete={() => deleteOtherDoc(d.doc_id)}
                />
              ))}
            </div>
            {canManage && (
              <div className="flex items-center gap-2 pt-1">
                <Input
                  data-testid="site-detail-other-label-input"
                  placeholder="Label (required)"
                  value={otherLabel}
                  onChange={(e) => setOtherLabel(e.target.value)}
                  className="h-8 text-xs max-w-[220px]"
                />
                <label
                  className={`inline-flex items-center gap-1.5 text-xs cursor-pointer ${uploading === "other" ? "text-slate-300" : "text-amber-600 hover:text-amber-700"}`}
                  data-testid="site-detail-other-upload"
                >
                  <Upload className="h-3.5 w-3.5" /> Upload
                  <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" disabled={uploading === "other"} onChange={(ev) => ev.target.files[0] && uploadOtherDoc(ev.target.files[0])} />
                </label>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
